'use client';

import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { persist, createJSONStorage } from 'zustand/middleware';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import { RegistrationMachine } from '@/store/pages/registrationStore/registrationMachine';
import { IRegistrationIdentityResponse } from '@/types/types';
import { applyDevtools, safeCookieStorage } from './../../storeUtils';
import { config } from '@/config';

interface RegistrationState {
    step: RegistrationStep;
    registrationId: string | null;
    howHeardId: number | null;
    yotiSessionID: string | null;
    yotiSessionToken: string | null;
    goToNextStep: () => void;
    goToPrevStep: () => void;
    goToStep: (registrationStep: RegistrationStep) => void;
    reset: () => void;
    setRegistrationId: (registrationId: string | null) => void;
    setHowHeardId: (howHeardId: number) => void;
    setYotiDetails: (data: IRegistrationIdentityResponse) => void;
}

const initialState: Partial<RegistrationState> = {
    step: RegistrationStep.PersonalDetails,
    registrationId: null,
};

export const useRegistrationStore = createWithEqualityFn<RegistrationState>()(
    persist(
        applyDevtools(
            (set, get) => ({
                step: RegistrationStep.PersonalDetails,
                registrationId: null,
                howHeardId: null,
                yotiSessionID: null,
                yotiSessionToken: null,
                goToStep: (registrationStep) => {
                    set(
                        { step: registrationStep },
                        false,
                        `Going to ${registrationStep.toString()} step`,
                    );
                },
                goToNextStep: () => {
                    const stepOptions = RegistrationMachine.getStepOptions(get().step);
                    if ('next' in stepOptions) {
                        set(
                            {
                                step: stepOptions.next,
                            },
                            false,
                            'Going to next step',
                        );
                    }
                },
                goToPrevStep: () => {
                    const stepOptions = RegistrationMachine.getStepOptions(get().step);
                    if ('prev' in stepOptions) {
                        set(
                            {
                                step: stepOptions.prev,
                            },
                            false,
                            'Going to prev step',
                        );
                    }
                },
                reset: () => set((state) => ({ ...state, ...initialState })),
                setRegistrationId: (registrationId) => {
                    set(
                        {
                            registrationId,
                        },
                        false,
                        'Setting of registration id',
                    );
                },
                setHowHeardId: (howHeardId) => {
                    set(
                        {
                            howHeardId,
                        },
                        false,
                        'Setting of how heard id',
                    );
                },
                setYotiDetails: ({ yotiSessionID, yotiSessionToken }) => {
                    set(
                        {
                            yotiSessionID,
                            yotiSessionToken,
                        },
                        false,
                        'Setting of Yoti session ID and token',
                    );
                },
            }),
            { name: `MPW ${config.theme} Registration Store` },
        ),
        {
            name: `mpw-${config.theme}-registration-storage`,
            storage: createJSONStorage(() => safeCookieStorage),
            partialize: (state) => ({
                registrationId: state.registrationId,
            }),
        },
    ),
    shallow,
);
